/**
 * Home: alerts sent to me (Narcan used, juvenile involved, someone hasn't signed in…) and the pass-on —
 * what the next shift should know, for the range each person picks.
 */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { ErrorBox, useAction, useLoad } from '../shared/ui.tsx';
import { ago, at } from '../ops/common.tsx';

export function AlertsCard() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/agency-alerts/mine'), []);
  const a = useAction();
  const list: any[] = r.data?.alerts ?? [];
  if (!list.length) return null;
  const seen = (id: string) => a.run(async () => (await api.post(`/api/agency-alerts/${id}/seen`), r.reload()));
  return (
    <section aria-labelledby="dh-alerts" className="dh-alerts">
      <h2 id="dh-alerts" className="dh-h">
        Alerts for you
      </h2>
      <ErrorBox error={a.error} />
      <ul className="dh-alert-list">
        {list.map((x) => (
          <li key={x.id} className={`card dh-alert k-${x.kind}`}>
            <div className="dh-alert-main">
              <span className="dh-alert-kind">{x.label}</span>
              <strong>{x.title}</strong>
              {x.body && <span className="small">{x.body}</span>}
              <span className="small muted">{ago(x.created_at)}</span>
            </div>
            <div className="dh-alert-act">
              {x.link && (
                <button type="button" className="btn small" onClick={() => navigate(at(x.link))}>
                  Open
                </button>
              )}
              <button type="button" className="btn small primary" disabled={a.busy} onClick={() => seen(x.id)}>
                Got it
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

const RANGES: [number, string][] = [
  [12, 'Last 12 hours'],
  [24, 'Last 24 hours'],
  [48, 'Last 48 hours'],
  [72, 'Last 3 days'],
  [168, 'Last 7 days'],
];
const KEY = 'bhq-passon-hours';
const savedHours = () => {
  try {
    const n = Number(localStorage.getItem(KEY));
    return RANGES.some(([h]) => h === n) ? n : 24;
  } catch {
    return 24;
  }
};

export function PassOnCard() {
  const { navigate } = useRouter();
  const [hours, setHours] = useState(savedHours);
  const r = useLoad(() => api.get<any>(`/api/passon?hours=${hours}`), [hours]);
  const a = useAction();
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState('');
  const [area, setArea] = useState('');
  const items: any[] = r.data?.items ?? [];
  const pick = (h: number) => {
    setHours(h);
    try {
      localStorage.setItem(KEY, String(h));
    } catch {
      /* fine */
    }
  };
  return (
    <section aria-labelledby="dh-passon" className="dh-passon">
      <div className="row between wrap">
        <h2 id="dh-passon" className="dh-h">
          Pass-on
        </h2>
        <div className="row gap-sm">
          <select value={hours} onChange={(e) => pick(Number(e.target.value))} aria-label="Pass-on from the">
            {RANGES.map(([h, l]) => (
              <option key={h} value={h}>
                {l}
              </option>
            ))}
          </select>
          {r.data?.canAdd && (
            <button type="button" className="btn small" onClick={() => setAdding(!adding)}>
              {adding ? 'Cancel' : '+ Add'}
            </button>
          )}
        </div>
      </div>
      {adding && (
        <form
          className="card passon-add"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => {
              await api.post('/api/passon', { note, area: area || null });
              setNote('');
              setArea('');
              setAdding(false);
              r.reload();
            });
          }}
        >
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should the next shift know?" aria-label="Pass-on note" maxLength={500} required />
          <input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Area (optional)" aria-label="Area" maxLength={120} />
          <button className="btn primary" disabled={a.busy || note.trim().length < 5}>
            Pass it on
          </button>
        </form>
      )}
      <ErrorBox error={a.error ?? r.error} />
      {r.data && !items.length ? (
        <div className="card dh-clear small muted">Nothing passed on in this time. Mark a report “Add to pass-on” when the next shift should know about it.</div>
      ) : (
        <ul className="card passon-list">
          {items.map((x) => (
            <li key={x.id}>
              <div>
                <strong>{x.note}</strong>
                <div className="small muted">
                  {[x.case_number && `Case ${x.case_number}`, x.title, x.area ?? x.location_text, x.by_name, ago(x.created_at)].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="row gap-sm">
                {x.canOpen && (
                  <button type="button" className="btn small" onClick={() => navigate(at(`/reports/${x.incident_id}`))}>
                    Report
                  </button>
                )}
                {x.canRemove && (
                  <button type="button" className="btn link small" aria-label={`Take “${x.note}” off the pass-on`} onClick={() => a.run(async () => (await api.post(`/api/passon/${x.id}/remove`), r.reload()))}>
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const escortWhen = (e: any) =>
  e.service_at ? new Date(e.service_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : e.service_text ? `${e.service_text} (check the date)` : 'No date given';

/** Funeral escorts coming up (next two weeks, and requests not staffed yet) — on everyone's Home. */
export function EscortsCard({ escorts, canManage }: { escorts: any[]; canManage: boolean }) {
  const { navigate } = useRouter();
  if (!escorts?.length) return null;
  return (
    <section aria-labelledby="dh-escorts" className="dh-escorts">
      <div className="row between wrap">
        <h2 id="dh-escorts" className="dh-h">
          Funeral escorts
        </h2>
        <button type="button" className="btn link" onClick={() => navigate(at('/escorts'))}>
          All ›
        </button>
      </div>
      <ul className="card dh-esc">
        {escorts.map((e) => (
          <li key={e.id} className={e.mine ? 'mine' : e.status === 'new' ? 'new' : ''}>
            <button type="button" onClick={() => navigate(at(`/escorts/${e.id}`))}>
              <span className="dh-esc-when">{escortWhen(e)}</span>
              <span>
                <strong>{e.deceased ? `For ${e.deceased}` : e.number}</strong>
                <span className="small muted"> · {[e.funeral_home, e.service_place && `service at ${e.service_place}`, e.cemetery && `to ${e.cemetery}`].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="small dh-esc-who">
                {e.mine ? (
                  <b>You’re on it</b>
                ) : e.status === 'new' ? (
                  <span className="dh-esc-need">{canManage ? 'Needs deputies' : 'Not staffed yet'}</span>
                ) : (
                  (e.deputy_list ?? []).map((p: any) => p.name).join(', ')
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
